import { useEffect, useMemo, useState } from 'react'

import { useParams } from 'react-router-dom'

import { useGetMetaAdsCampaignByIdQuery } from 'src/Redux/adsCampaignApi'
import { SpotifyArtist } from 'src/Redux/spotifyArtistsApi'

type UseSelectedArtistsReturnType = {
  selectedArtists: SpotifyArtist[]
  onSetSelectedArtists: (artist: SpotifyArtist) => void
  hasChanges: boolean
}

const mapArtistNamesToSelectedArtists = (
  artistNames?: string[],
): SpotifyArtist[] => artistNames?.map((name) => ({ name })) ?? []

const normalizeArtistName = (name: string): string =>
  name.trim().toLocaleLowerCase()

export const useSelectedArtists = (): UseSelectedArtistsReturnType => {
  const { number: id } = useParams<{ number: string }>()
  const { data: campaignDetails } = useGetMetaAdsCampaignByIdQuery(Number(id))

  const initialArtists = campaignDetails?.similarArtistNames

  const [selectedArtists, setSelectedArtists] = useState<SpotifyArtist[]>(() =>
    mapArtistNamesToSelectedArtists(initialArtists),
  )

  useEffect(() => {
    setSelectedArtists(mapArtistNamesToSelectedArtists(initialArtists))
  }, [initialArtists])

  const hasChanges = useMemo(() => {
    if (initialArtists?.length !== selectedArtists.length) {
      return true
    }

    const initialNames = (initialArtists ?? [])
      .map(normalizeArtistName)
      .sort((a, b) => a.localeCompare(b))

    const selectedNames = selectedArtists
      .map((artist) => normalizeArtistName(artist.name))
      .sort((a, b) => a.localeCompare(b))

    return !initialNames.every((name, index) => name === selectedNames[index])
  }, [initialArtists, selectedArtists])

  const handleSelectedArtists = (artist: SpotifyArtist): void => {
    setSelectedArtists((prev) => {
      const isAlreadySelected = prev.some(
        (item) =>
          normalizeArtistName(item.name) === normalizeArtistName(artist.name),
      )

      return isAlreadySelected
        ? prev.filter(
            (item) =>
              normalizeArtistName(item.name) !==
              normalizeArtistName(artist.name),
          )
        : [...prev, artist]
    })
  }

  return {
    selectedArtists,
    hasChanges,
    onSetSelectedArtists: handleSelectedArtists,
  }
}
